import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { typographyStyles } from '~/components/__utils__/component-utils';
import '~/components/rating/rating';
import '~/components/icon/icon';
import '~/components/badge/badge';
import '~/components/save-button/save-button';

import { IconName } from '~/components/__utils__/iconRegistry';

export type AccommodationFeatures = {
  occupancy?: number;
  bedrooms?: number;
  hotTub?: boolean;
  petsAllowed?: boolean;
};

export type AccommodationCardProps = {
  name: string;
  location: string;
  imageUrl: string;
  price: number;
  priceLabel?: string;
  features?: AccommodationFeatures;
  rating?: number;
  slug: string;
  orientation?: 'vertical' | 'horizontal';
};

type FeatureConfig = {
  icon: IconName;
  label: (value: number | boolean) => string;
};

const featureMap: Record<keyof AccommodationFeatures, FeatureConfig> = {
  occupancy: {
    icon: 'persons',
    label: (value) => `${value} Personen`,
  },
  bedrooms: {
    icon: 'bedrooms',
    label: (value) => `${value} slaapkamer${value !== 1 ? 's' : ''}`,
  },
  hotTub: {
    icon: 'hot-tub',
    label: () => 'Hottub',
  },
  petsAllowed: {
    icon: 'dog',
    label: () => 'Honden toegestaan',
  },
};

@customElement('dvg-accommodation-card')
export class AccommodationCard extends LitElement implements AccommodationCardProps {

  @property({ type: String }) name: string = '';
  @property({ type: String }) location: string = '';
  @property({ type: String, attribute: 'image-url' }) imageUrl: string = '';
  @property({ type: Number }) price: number = 0;
  @property({ type: String, attribute: 'price-label' }) priceLabel?: string;
  @property({
    type: Object,
    converter: {
      fromAttribute(value: string): AccommodationFeatures {
        try {
          return JSON.parse(value) as AccommodationFeatures;
        } catch {
          return {};
        }
      },
      toAttribute(value: AccommodationFeatures): string {
        return JSON.stringify(value);
      }
    }
  }) features: AccommodationFeatures = {};
  @property({ type: Number }) rating?: number;
  @property({ type: String }) slug: string = '#';
  @property({ type: String }) orientation: 'vertical' | 'horizontal' = 'vertical';

  private formatPrice(price: number): string {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  }

  private handleSaveClick(e: Event) {
    e.preventDefault();
    e.stopPropagation();
  }

  private renderFeatures() {
    const featureEntries = Object.entries(this.features) as [keyof AccommodationFeatures, number | boolean | undefined][];

    return featureEntries
      .filter(([, value]) => value !== undefined && value !== false && value !== 0)
      .map(([key, value]) => {
        const config = featureMap[key];
        if (!config) return null;

        return html`
          <span class="feature-chip">
            <dvg-icon name="${config.icon}" size="1.25rem"></dvg-icon>
            <span>${config.label(value as number | boolean)}</span>
          </span>
        `;
      });
  }

  render() {
    const imageUrl = this.imageUrl || `${window.DVGBookingConfig.pluginUrl}/assets/images/devakantiegids-logo-icon-green.svg`;

    return html`
      <article class="accommodation-card ${this.orientation}">
        <div class="image-wrapper">
          <dvg-badge class="type-badge" label="Vakantiehuisje" theme="secondary" hide-earmark></dvg-badge>
          <dvg-save-button class="save-button" @click="${this.handleSaveClick}"></dvg-save-button>
          <a href="${this.slug}" class="image-link">
            <img
              class="image${!this.imageUrl ? ' placeholder' : ''}"
              src="${imageUrl}"
              alt="${this.name}"
              loading="lazy"
            />
          </a>
        </div>

        <div class="content">
          <div class="location-row">
            <span class="location">
              <dvg-icon name="marker" size="0.875rem"></dvg-icon>
              <span>${this.location}</span>
            </span>
            ${this.rating ? html`<dvg-rating class="rating" value="${this.rating}" size="0.875rem"></dvg-rating>` : ''}
          </div>

          <h3 class="title">${this.name}</h3>

          <div class="features">
            ${this.renderFeatures()}
          </div>

          <div class="price-row">
            <div class="price-wrapper">
              <span class="price">${this.formatPrice(this.price)}</span>
              ${this.priceLabel ? html`<span class="price-label">/${this.priceLabel}</span>` : ''}
            </div>
            <a href="${this.slug}" class="cta-button">
              <span>Vakantiehuisje bekijken</span>
            </a>
          </div>
        </div>
      </article>
    `;
  }

  static styles = [typographyStyles, css`
    :host {
      display: block;
      box-sizing: border-box;
      height: 100%;
      width: 100%;
      container: card / inline-size;
    }

    *, *:before, *:after {
      box-sizing: inherit;
    }

    .accommodation-card {
      width: 100%;
      height: 100%;
      margin: 0;
      background: var(--dvg-white);
      border-radius: 2rem;
      overflow: hidden;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
      transition: box-shadow 0.3s ease, transform 0.3s ease;
      padding: .8rem;

      display: grid;
      gap: 1rem;

      grid-template-areas:
          "image"
          "content";
      grid-template-columns: 1fr;
      grid-template-rows: 16rem 1fr;

      &:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        transform: translateY(-4px);
      }

      &:hover .image:not(.placeholder) {
        transform: scale(1.05);
      }

      &.horizontal {
        @container card (min-width: 500px) {
          grid-template-areas:
            "image content";
          grid-template-columns: 14rem 1fr;
          grid-template-rows: 1fr;
        }
      }
    }

    .image-wrapper {
      grid-area: image;
      overflow: hidden;
      position: relative;
      display: block;
      border-radius: 1rem;
    }

    .image-link {
      display: block;
      width: 100%;
      height: 100%;
    }

    .image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }

    .image.placeholder {
      object-fit: contain;
      transform: scale(0.5);
      opacity: 0.3;
      filter: grayscale(100%);
    }

    .type-badge {
      position: absolute;
      top: 1rem;
      left: .5rem;
      z-index: 2;
    }

    .save-button {
      position: absolute;
      top: 1rem;
      right: .5rem;
      z-index: 2;
    }

    .content {
      grid-area: content;
      padding: 0 .5rem .75rem;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .location-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
    }

    .rating {
      flex-shrink: 0;
    }

    .location {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      color: #6B7280;
      font-size: 0.8125rem;
    }

    .title {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 700;
      color: var(--dvg-black);
      line-height: 1.3;
    }

    .features {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem 1.5rem;
      flex-grow: 1;
    }

    .feature-chip {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      background: transparent;
      color: var(--dvg-black);
      font-size: 0.875rem;
      font-weight: 300;
    }

    .price-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      margin-top: auto;
    }

    .price-wrapper {
      display: flex;
      align-items: baseline;
      gap: 0.125rem;
    }

    .price {
      font-size: 1.75rem;
      font-weight: 700;
      color: var(--dvg-green);
    }

    .price-label {
      font-size: .75rem;
      color: #6B7280;
    }

    .cta-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 0.75rem 1.25rem;
      background: transparent;
      border: 1px solid var(--dvg-orange);
      border-radius: 2rem;
      color: var(--dvg-orange);
      font-size: .75rem;
      font-weight: 400;
      cursor: pointer;
      transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
      text-decoration: none;
      white-space: nowrap;

      &:hover {
        background: var(--dvg-orange);
        color: var(--dvg-white);
        transform: translateY(-1px);
      }
    }
  `];
}

declare global {
  interface HTMLElementTagNameMap {
    'dvg-accommodation-card': AccommodationCard;
  }
}
